// ── Dock parts catalog ───────────────────────────────────────────────────────────────────────
// Every dock part type is defined here once, as data: its parameters (defaults, ranges, steps),
// its connection ports, its physical data and the bill-of-materials lines it brings. Assemblies
// store references to parts (part id + catalog version + parameters), never copies of geometry,
// so a change here reaches every dock that uses the part the next time it is built.
//
// Versioning: the catalog is a list of RELEASES, each a complete snapshot. A change is a new
// release (copy the last one, change it, add a changelog note) -- never an edit to a published
// release, so an assembly pinned to an older version (an issued drawing set, an as-built record)
// always rebuilds exactly as it was drawn. Unpinned assemblies follow the latest release.
//
// All lengths in feet, elevations in feet on the project's vertical datum. Generic names only
// (no manufacturer trademarks). Dimensions the market doesn't publish are marked approx: true --
// the first defaults come from the Loch Lomond Marina (LLM) SLAM scan, 2026-05-12.

const p = (value, min, max, step, unit, label, extra = {}) => ({ default: value, min, max, step, unit, label, ...extra });
const r2 = (x) => Math.round(x * 100) / 100; // tidy numbers in BOM specs

const WALER_CONCRETE_V1 = {
  label: "Concrete floats, waler-connected",
  notes: "Precast concrete float modules joined by timber walers and thru-rods; fingers on steel knee-braced frames; square concrete piles in internal wells or external guides. Defaults measured at LLM dock G.",
  parts: {
    "walkway-module": {
      label: "Walkway float module",
      kind: "float",
      params: {
        length: p(20, 4, 20, 0.5, "ft", "Length", { approx: true }),
        width: p(9, 4, 16, 0.5, "ft", "Width"), // LLM dock G: 9.1 ft deck, 81 cross-sections
        freeboard: p(2.7, 1, 3.5, 0.05, "ft", "Freeboard"),
        depth: p(4, 2, 6, 0.1, "ft", "Float depth", { approx: true }),
        walerHeight: p(0.83, 0.5, 1.25, 0.01, "ft", "Waler height", { approx: true }),
        walerThickness: p(0.25, 0.15, 0.5, 0.01, "ft", "Waler thickness", { approx: true }),
        rodSpacing: p(4, 2, 8, 0.5, "ft", "Thru-rod spacing", { approx: true }),
      },
      ports: ["start", "end", "left", "right"],
      bom: (q) => [
        { item: "Concrete float module", spec: `${q.length} x ${q.width} ft`, qty: 1, unit: "ea" },
        { item: "Timber waler", spec: `${r2(q.walerThickness * 12)} x ${r2(q.walerHeight * 12)} in.`, qty: 2 * q.length, unit: "lf" },
        { item: "Thru-rod", spec: `${r2(q.width + 2 * q.walerThickness + 0.5)} ft`, qty: Math.floor(q.length / q.rodSpacing) + 1, unit: "ea" },
        { item: "Deck area", spec: "concrete", qty: q.length * q.width, unit: "sf" },
      ],
    },
    "corner-module": {
      label: "Corner float module",
      kind: "float",
      params: {
        width: p(9, 4, 16, 0.5, "ft", "Width"),
        freeboard: p(2.7, 1, 3.5, 0.05, "ft", "Freeboard"),
        depth: p(4, 2, 6, 0.1, "ft", "Float depth", { approx: true }),
      },
      ports: ["a", "b"],
      bom: (q) => [
        { item: "Concrete float module, corner", spec: `${q.width} x ${q.width} ft`, qty: 1, unit: "ea" },
        { item: "Deck area", spec: "concrete", qty: q.width * q.width, unit: "sf" },
      ],
    },
    finger: {
      label: "Finger",
      kind: "float",
      params: {
        length: p(46, 8, 80, 0.5, "ft", "Length"), // LLM dock G: 45.9 ft from the walkway edge, 28 fingers
        width: p(4, 2.5, 8, 0.25, "ft", "Width"),
        freeboard: p(2.8, 1, 3.5, 0.05, "ft", "Freeboard"),
        depth: p(3.5, 2, 6, 0.1, "ft", "Float depth", { approx: true }),
        tip: p("round", null, null, null, "", "Tip", { options: ["round", "square"] }),
        walerHeight: p(0.83, 0.5, 1.25, 0.01, "ft", "Waler height", { approx: true }),
        walerThickness: p(0.25, 0.15, 0.5, 0.01, "ft", "Waler thickness", { approx: true }),
        rodSpacing: p(4, 2, 8, 0.5, "ft", "Thru-rod spacing", { approx: true }),
      },
      ports: ["root", "tip"],
      bom: (q) => [
        { item: "Concrete finger float", spec: `${q.length} x ${q.width} ft, ${q.tip} tip`, qty: 1, unit: "ea" },
        { item: "Timber waler", spec: `${r2(q.walerThickness * 12)} x ${r2(q.walerHeight * 12)} in.`, qty: 2 * q.length, unit: "lf" },
        { item: "Thru-rod", spec: `${r2(q.width + 2 * q.walerThickness + 0.5)} ft`, qty: Math.floor(q.length / q.rodSpacing) + 1, unit: "ea" },
        { item: "Deck area", spec: "concrete", qty: q.length * q.width, unit: "sf" },
      ],
    },
    "finger-frame": {
      label: "Finger frame (knee-braced)",
      kind: "connector",
      params: {
        kneeAlongWalk: p(4, 0, 14, 0.5, "ft", "Knee length along walkway"), // LLM dock G: 4 ft each side
        kneeAlongFinger: p(5.5, 0, 12, 0.5, "ft", "Knee length along finger"), // LLM dock G: 5.5 ft
        walkRods: p(4, 2, 6, 1, "ea", "Walkway thru-rods engaged"),
        fingerRods: p(6, 2, 8, 1, "ea", "Finger thru-rods engaged"),
      },
      ports: ["walk", "finger"],
      joint: "rigid",
      bom: (q) => [
        { item: "Steel finger frame (tri-frame) with knee braces", spec: `${q.kneeAlongWalk} / ${q.kneeAlongFinger} ft knees`, qty: 1, unit: "ea" },
      ],
    },
    pile: {
      label: "Pile",
      kind: "pile",
      params: {
        section: p("square", null, null, null, "", "Section", { options: ["square", "round"] }),
        size: p(16, 10, 36, 1, "in", "Size (width or diameter)", { approx: true }),
        material: p("concrete", null, null, null, "", "Material", { options: ["concrete", "steel", "timber"] }),
        topElev: p(16, 5, 30, 0.1, "ft", "Top elevation"),
        tipElev: p(null, -120, 0, 0.5, "ft", "Tip elevation (unknown until surveyed)"),
      },
      ports: ["guide"],
      bom: (q) => [
        { item: `${q.material[0].toUpperCase()}${q.material.slice(1)} pile, ${q.section}`, spec: `${q.size} in.${q.tipElev != null ? `, ${r2(q.topElev - q.tipElev)} ft long` : ", length to be set from the survey"}`, qty: 1, unit: "ea" },
      ],
    },
    "pile-guide": {
      label: "Pile guide",
      kind: "connector",
      params: {
        type: p("internal", null, null, null, "", "Type", { options: ["internal", "external"] }),
        clearance: p(0.75, 0.25, 2, 0.25, "in", "Clearance to pile"),
      },
      ports: ["pile", "float"],
      joint: "slide-vertical",
      bom: (q) => [
        { item: q.type === "internal" ? "Internal pile well with UHMW pads" : "External pile hoop with UHMW pads", spec: `${q.clearance} in. clearance`, qty: 1, unit: "ea" },
      ],
    },
    // ── fittings: mounted on a float's deck, so they ride the water with it ──
    "power-pedestal": {
      label: "Power post",
      kind: "fitting",
      params: {
        height: p(3.6, 2.5, 5, 0.1, "ft", "Height", { approx: true }),
        width: p(0.75, 0.5, 1.5, 0.05, "ft", "Width"), // LLM: ~1.5 ft deck gap at each finger root (post + base)
        depth: p(0.6, 0.4, 1.2, 0.05, "ft", "Depth"),
        outlets: p(2, 1, 4, 1, "ea", "Slips served"),
      },
      bom: (q) => [{ item: "Power pedestal with light", spec: `${q.outlets} slips, ${r2(q.height)} ft`, qty: 1, unit: "ea" }],
    },
    "dock-box": {
      label: "Dock box",
      kind: "fitting",
      params: {
        length: p(3, 2, 6, 0.25, "ft", "Length"), // LLM dock G: ~3 ft long, 2.4 ft tall
        width: p(2, 1.5, 3, 0.25, "ft", "Width"),
        height: p(2.4, 1.5, 3, 0.1, "ft", "Height"),
      },
      bom: (q) => [{ item: "Dock box, fiberglass", spec: `${r2(q.length)} x ${r2(q.width)} x ${r2(q.height)} ft`, qty: 1, unit: "ea" }],
    },
    cleat: {
      label: "Cleat",
      kind: "fitting",
      params: { length: p(1, 0.5, 2, 0.25, "ft", "Length") },
      bom: (q) => [{ item: "Cleat, galvanized", spec: `${q.length * 12} in.`, qty: 1, unit: "ea" }],
    },
  },
  // Layout rules the generator and the drawing tools use; user-adjustable per assembly.
  rules: {
    moduleSplit: "standard-then-remainder", // full-length modules first, the remainder last
    minModuleLength: 8,
    autoPiles: { fingerTips: true, walkSpacing: 40, walkSides: "alternate" },
    pileGuideReach: 1.5, // ft beyond a float's edge that still counts as guiding it (external hoop)
  },
};

// v2: connection points between separate dock features (T/cross junctions), gangways (shore
// ramps) with a slope check, access gates, a T-head finger tip, and the fittings a marina needs
// beyond power and a place to tie off -- mooring whips, fire/life-safety stations, perimeter
// fencing, security lighting, trash receptacles. See docs/research/dock-builder-pro.md for the
// wider survey this draws from (ABYC, NFPA 303, ADA 2010 recreational boating facilities).
const WALER_CONCRETE_V2 = {
  ...WALER_CONCRETE_V1,
  parts: {
    ...WALER_CONCRETE_V1.parts,
    finger: {
      ...WALER_CONCRETE_V1.parts.finger,
      params: { ...WALER_CONCRETE_V1.parts.finger.params, tip: p("round", null, null, null, "", "Tip", { options: ["round", "square", "T-head"] }) },
    },
    // ── connection points: joins a branch walkway to the side of a host walkway (T or + junctions) ──
    "walkway-junction": {
      label: "Walkway junction (T/cross)",
      kind: "connector",
      params: {
        armLength: p(4, 0, 12, 0.5, "ft", "Brace length along the host"),
      },
      ports: ["host", "branch"],
      joint: "rigid",
      bom: (q) => [{ item: "Steel junction frame (knee-braced)", spec: `${q.armLength} ft braces`, qty: 1, unit: "ea" }],
    },
    // ── gangway: the fixed shore ramp onto a floating dock ──
    gangway: {
      label: "Gangway",
      kind: "ramp",
      notes: "Aluminum truss ramp, shore end pinned/hinged, dock end on a roller carriage so the ramp rides the tide range without binding.",
      params: {
        width: p(4, 3, 8, 0.5, "ft", "Width"),
        shoreRise: p(4, -2, 12, 0.5, "ft", "Shore landing height above the dock deck", { approx: true }),
        handrailHeight: p(3, 2.5, 3.5, 0.05, "ft", "Handrail height"),
        surface: p("grating", null, null, null, "", "Surface", { options: ["grating", "solid", "non-skid-rubber"] }),
      },
      ports: ["shore", "dock"],
      // q.length is set by the generator from the drawn shore-to-dock distance, not a catalog param.
      bom: (q) => [
        { item: "Aluminum gangway truss", spec: `${q.width} ft wide, ${Math.ceil(q.length || 0)} ft long, ${q.surface} surface`, qty: 1, unit: "ea" },
        { item: "Aluminum handrail", spec: `${r2(q.handrailHeight)} ft`, qty: 2 * Math.ceil(q.length || 0), unit: "lf" },
        { item: "Dock-end roller carriage", spec: "rides the tide range", qty: 1, unit: "ea" },
      ],
    },
    // ── gate: spans a float's full width to control who walks onto the dock ──
    gate: {
      label: "Access gate",
      kind: "gate",
      params: {
        swing: p("single", null, null, null, "", "Type", { options: ["single", "double", "slide"] }),
        heightFt: p(4, 3, 6, 0.5, "ft", "Height"),
        access: p("card", null, null, null, "", "Access control", { options: ["key", "code", "card", "none (latch only)"] }),
      },
      bom: (q) => [
        { item: `Marina access gate, ${q.swing}`, spec: `${r2(q.heightFt)} ft tall`, qty: 1, unit: "ea" },
        ...(q.access !== "none (latch only)" ? [{ item: "Gate access control reader", spec: q.access, qty: 1, unit: "ea" }] : []),
      ],
    },
    // ── fittings: the rest of what a marina deck carries, beyond power and a place to tie off ──
    "mooring-whip": {
      label: "Mooring whip",
      kind: "fitting",
      notes: "Flexible fiberglass pole that holds a boat off an open-faced slip or seawall without a finger dock.",
      params: {
        length: p(10, 6, 16, 0.5, "ft", "Length"),
        capacity: p(6000, 2000, 20000, 500, "lb", "Vessel capacity", { approx: true }),
      },
      bom: (q) => [{ item: "Mooring whip, fiberglass", spec: `${q.length} ft, ${q.capacity} lb`, qty: 1, unit: "ea" }],
    },
    "fire-station": {
      label: "Fire station",
      kind: "fitting",
      notes: "NFPA 303 marina fire protection: a cabinet with an extinguisher and, on larger docks, a hose and standpipe valve.",
      params: {
        type: p("extinguisher", null, null, null, "", "Type", { options: ["extinguisher", "hose cabinet"] }),
      },
      bom: (q) => [{ item: q.type === "hose cabinet" ? "Fire hose cabinet with standpipe valve" : "Fire extinguisher cabinet", spec: "NFPA 303", qty: 1, unit: "ea" }],
    },
    "life-ring-station": {
      label: "Life ring station",
      kind: "fitting",
      notes: "Ring buoy and throw line on a pole, within reach of every slip per most state marina codes.",
      params: {},
      bom: () => [{ item: "Life ring station (ring buoy + throw line)", spec: "pole mounted", qty: 1, unit: "ea" }],
    },
    "fence-panel": {
      label: "Fence panel",
      kind: "fitting",
      notes: "Perimeter security fencing along a float's edge, for gated marinas and liability/insurance fencing requirements.",
      params: {
        length: p(8, 4, 12, 1, "ft", "Length"),
        heightFt: p(4, 3, 6, 0.5, "ft", "Height"),
        mesh: p("chain-link", null, null, null, "", "Infill", { options: ["chain-link", "picket", "mesh-privacy"] }),
      },
      bom: (q) => [{ item: `Perimeter fence panel, ${q.mesh}`, spec: `${q.length} x ${r2(q.heightFt)} ft`, qty: 1, unit: "ea" }],
    },
    "security-light": {
      label: "Security light",
      kind: "fitting",
      notes: "Pole-mounted dock light on a photocell, with an optional camera head for gated-marina security.",
      params: {
        height: p(8, 6, 12, 0.5, "ft", "Height"),
        // a <select>'s value is always a string, so this is "yes"/"no", not a boolean -- see resolveParams
        camera: p("no", null, null, null, "", "Camera head", { options: ["no", "yes"] }),
      },
      bom: (q) => [{ item: q.camera === "yes" ? "Security light + camera pole" : "Dock light pole", spec: `${r2(q.height)} ft`, qty: 1, unit: "ea" }],
    },
    "trash-receptacle": {
      label: "Trash / recycling receptacle",
      kind: "fitting",
      params: {
        twoStream: p("yes", null, null, null, "", "Trash + recycling", { options: ["yes", "no"] }),
      },
      bom: (q) => [{ item: q.twoStream === "yes" ? "Trash + recycling receptacle, dual" : "Trash receptacle", spec: "dock-mounted", qty: 1, unit: "ea" }],
    },
  },
  rules: {
    ...WALER_CONCRETE_V1.rules,
    gangwaySlope: { adaMax: 1 / 12, generalMax: 1 / 4 }, // rise/run: ADA 2010 accessible route vs. a typical non-accessible marina gangway
  },
};

// Releases: append only. Each is a complete snapshot (see the header).
export const CATALOG_RELEASES = [
  {
    version: 1,
    date: "2026-10-09",
    notes: "First catalog: waler-connected concrete floats (walkway module, corner module, finger, finger frame, pile, pile guide), defaults from LLM dock G.",
    systems: { "waler-concrete": WALER_CONCRETE_V1 },
  },
  {
    version: 2,
    date: "2026-10-22",
    notes: "Connection points (walkway-junction for T/cross docks), gangway (shore ramp, with a slope check), access gate, T-head finger tip, and new fittings: mooring whip, fire station, life ring station, fence panel, security light, trash receptacle.",
    systems: { "waler-concrete": WALER_CONCRETE_V2 },
  },
];

export const LATEST_CATALOG_VERSION = CATALOG_RELEASES[CATALOG_RELEASES.length - 1].version;

// The catalog an assembly builds with: its pinned version, else the latest.
export function catalogAt(version) {
  if (version == null) return CATALOG_RELEASES[CATALOG_RELEASES.length - 1];
  const rel = CATALOG_RELEASES.find((r) => r.version === Number(version));
  if (!rel) throw new Error(`no catalog release ${version}`);
  return rel;
}

export function getSystem(systemId, version) {
  const sys = catalogAt(version).systems[systemId];
  if (!sys) throw new Error(`no dock system "${systemId}" in catalog ${version ?? LATEST_CATALOG_VERSION}`);
  return sys;
}

// A part's full parameter set: catalog defaults, overlaid with the instance's own values. New
// parameters added in a later release take their default, so older instances pick them up.
export function resolveParams(part, values = {}) {
  const out = {};
  for (const [k, def] of Object.entries(part.params)) {
    const v = values[k];
    if (v === undefined || v === null || v === "") { out[k] = def.default; continue; }
    if (def.options) { out[k] = def.options.includes(v) ? v : def.default; continue; }
    const n = Number(v);
    out[k] = Number.isFinite(n) ? Math.min(def.max ?? n, Math.max(def.min ?? n, n)) : def.default;
  }
  return out;
}
